import React from 'react';
import { Clock, Target, Medal } from 'lucide-react';
import { HourlyMetrics, TimeMetricType, PeakHourAnalysis } from './types';
import {
  generateTimeInsights,
  formatHour,
  formatTimeMetricValue,
  TIME_COLOR_SCHEMES,
  TIME_METRIC_COLORS,
} from './utils';

interface TimeInsightsPanelProps {
  data: HourlyMetrics[];
  selectedMetric: TimeMetricType;
  peakAnalysis: PeakHourAnalysis;
  use24Hour?: boolean;
  className?: string;
}

const TimeInsightsPanel: React.FC<TimeInsightsPanelProps> = ({
  data,
  selectedMetric,
  peakAnalysis,
  use24Hour = true,
  className = '',
}) => {
  const insights = generateTimeInsights(data, selectedMetric);
  const colorScheme = TIME_COLOR_SCHEMES[TIME_METRIC_COLORS[selectedMetric]];

  return (
    <div className={`p-4 pb-2 px-6 ${className}`}>
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Peak Hour */}
        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
          <div className="flex items-center gap-2 mb-2">
            <Medal className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-900">
              Peak Hour
            </span>
          </div>
          <div className="text-2xl font-bold text-orange-900">
            {formatHour(peakAnalysis.peakHour, use24Hour)}
          </div>
          <div className="text-sm text-orange-700">
            {formatTimeMetricValue(peakAnalysis.peakValue, selectedMetric)}
          </div>
        </div>

        {/* Average */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              Average
            </span>
          </div>
          <div className="text-2xl font-bold text-blue-900">
            {formatTimeMetricValue(peakAnalysis.averageValue, selectedMetric)}
          </div>
          <div className="text-sm text-blue-700">
            Across all hours
          </div>
        </div>

        {/* Best Period */}
        <div
          className="rounded-lg p-4 border"
          style={{
            backgroundColor: colorScheme.background,
            borderColor: colorScheme.secondary,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock
              className="w-4 h-4"
              style={{ color: colorScheme.primary }}
            />
            <span
              className="text-sm font-medium"
              style={{ color: colorScheme.primary }}
            >
              Best Period
            </span>
          </div>
          <div
            className="text-2xl font-bold"
            style={{ color: colorScheme.primary }}
          >
            {insights.bestPeriod.name}
          </div>
          <div
            className="text-sm"
            style={{ color: colorScheme.primary }}
          >
            {insights.bestPeriod.hours}
          </div>
        </div>
      </div>

      {/* Period Performance */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-gray-900 mb-3">
          Performance by Time Period
        </h4>
        <div className="space-y-2">
          {insights.periods.map((period, index) => {
            const isTop = index === 0;
            const percentage = (period.average / insights.periods[0].average) * 100;

            return (
              <div
                key={period.name}
                className="flex items-center gap-3"
              >
                <div className="w-20 text-sm font-medium text-gray-700">
                  {period.name}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: isTop ? colorScheme.primary : colorScheme.secondary,
                        }}
                      />
                    </div>
                    <div className="w-20 text-sm text-gray-600">
                      {formatTimeMetricValue(period.average, selectedMetric)}
                    </div>
                  </div>
                </div>
                <div className="w-16 text-xs text-gray-500">
                  {period.hours}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimeInsightsPanel;
